import { cloneDeep } from "lodash";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { AnalyticsResourceType } from "@intelligems/ig-types";

import { useConfiguration } from "../../../api/configurations/use-configuration";
import { useGraphValueFormatter } from "../../../api/formatters/dashboard";
import { selectedVariations } from "../../../store/slices/variations";
import theme from "../../../theme";
import { StackedAreaMetricData } from "../../../types/analytics";
import { ExperienceFe } from "../../../types/schemas/experiences";
import { sortByOrder } from "../../../utils/sortings";

import { GraphConfig } from "./Utils/graphConfigs";
import { groupColors } from "./Utils/graphHelpers";
import {
  CustomizedAxisTick,
  CustomResponsiveContainer,
  StyledLegendText,
} from "./Utils/graphStyleCustoms";

interface StackedAreaGraphProps {
  config: GraphConfig;
  groupMetricData: StackedAreaMetricData[];
  resource?: ExperienceFe;
  resourceType: AnalyticsResourceType;
}

export const StackedAreaGraph = ({
  config,
  groupMetricData,
  resource,
  resourceType,
}: StackedAreaGraphProps) => {
  const variations = useSelector(selectedVariations);
  const graphValueFormatter = useGraphValueFormatter();
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");

  const dataMin = config.yAxisDataMin ?? "dataMin";

  const areaMapping: string[] = useMemo(() => {
    if (resourceType === "experiences" && resource) {
      return cloneDeep(variations)
        .sort(sortByOrder)
        .map((group) => group.id);
    } else {
      return cloneDeep(config.metrics); // currently used for sitewide analytics
    }
  }, [resource, resourceType, config, variations]);

  if (storeCurrencyConfig && storeCurrencyConfig.value) {
    return (
      <CustomResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={groupMetricData}
          style={{ marginLeft: -24 }}
          margin={{
            top: 5,
            bottom: 5,
          }}
        >
          <CartesianGrid
            style={{ stroke: theme.palette.disabled.light }}
            vertical={false}
          />
          <XAxis
            dataKey={"xAxisKey"}
            axisLine={{ stroke: theme.palette.disabled.light }}
            tickLine={false}
            tick={(props: any) => {
              return (
                // @ts-ignore
                <CustomizedAxisTick
                  props={props}
                  groupMetricData={groupMetricData}
                  graphValueFormatter={graphValueFormatter}
                  config={config}
                />
              );
            }}
          />
          <YAxis
            domain={[dataMin, "dataMax"]}
            style={{ fontFamily: theme.typography.fontFamily, fontSize: 14 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value: any) =>
              graphValueFormatter(true, value, config)!
            }
          />
          <Tooltip
            contentStyle={{
              fontFamily: theme.typography.fontFamily,
              borderRadius: 5,
              fontSize: 14,
              backgroundColor: "rgba(97, 97, 97, 0.92)",
              color: "#FFF",
              border: "1px solid transparent",
            }}
            itemStyle={{ color: "#FFF" }}
            cursor={{ fill: "transparent" }}
            formatter={(value: any, name: any) => {
              if (config.legendFormatFunction) {
                return [
                  graphValueFormatter(false, value, config)!,
                  config.legendFormatFunction(
                    variations.find((v) => v.id === name)?.name
                  ),
                ];
              } else {
                return [
                  graphValueFormatter(false, value, config)!,
                  variations.find((v) => v.id === name)?.name,
                ];
              }
            }}
          />
          {areaMapping.map((value, index) => (
            <Area
              fillOpacity={1}
              key={value}
              type="monotone"
              dataKey={value}
              stackId="1"
              stroke={groupColors[index]}
              fill={groupColors[index]}
            />
          ))}
          <Legend
            wrapperStyle={{
              paddingLeft: "25px",
              paddingTop: "10px",
            }}
            align={"center"}
            formatter={(value, entry, index) => {
              return StyledLegendText(
                variations.find((v) => v.id === value)?.name || "",
                entry,
                index,
                config?.legendFormatFunction
              );
            }}
            iconType={"circle"}
            iconSize={9}
          />
        </AreaChart>
      </CustomResponsiveContainer>
    );
  } else return null;
};
